import type { PackageTypeIconKey } from '@pcrm/shared/icons';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFileUrl, apiGet, apiPatch, apiPost, apiUpload } from '../../lib/api';

/**
 * Zona de administrare, dinspre client.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/settings`: `apps/crm` e
 * `scope:fe` și n-are voie să depindă de backend. E aceeași alegere ca la
 * `features/auth/session.ts` — granița se validează în ambele capete, iar un câmp
 * redenumit pe server pică la `parse`, nu ajunge `undefined` în UI.
 */

const settingSchema = z.object({
  /** `null` = setare declarată în cod, care încă n-are rând în bază. */
  id: z.uuid().nullable(),
  name: z.string(),
  value: z.string().nullable(),
  /**
   * `z.string()`, nu `z.enum(['text', 'file'])`.
   *
   * ⚠️ Aceeași alegere ca la iconul nomenclatoarelor: dacă serverul capătă un tip nou
   * (`date`, `boolean`) înaintea unui frontend încă nedeployat, un `enum` ar face **tot
   * răspunsul** să pice la `parse` — adică un ecran de setări gol din cauza unui rând.
   * Un tip necunoscut cade pe randarea ca text, unde costă un câmp, nu ecranul.
   */
  type: z.string(),
  /**
   * Clipa ultimei salvări, ISO. Opțional la `parse`, ca `dated`: un server încă nedeployat
   * nu le trimite, iar ecranul de setări nu trebuie să cadă din cauza asta.
   */
  updatedAt: z.string().nullable().optional(),
  /**
   * Valoarea poartă data ei (cursul valutar, #185): rândul arată ziua, iar sertarul lasă
   * salvarea aceleiași valori — o reconfirmă pentru azi.
   */
  dated: z.boolean().optional(),
});

export type Setting = z.infer<typeof settingSchema>;

/** Setarea ține un fișier, deci se schimbă prin încărcare, nu prin formular. */
export function isFileSetting(setting: Setting): boolean {
  return setting.type === 'file';
}

/**
 * Adresa fișierului, pentru `<img src>`.
 *
 * `cacheBuster` e **cheia din storage**, care conține hash-ul conținutului: o semnătură
 * înlocuită are altă cheie, deci altă adresă, deci browserul chiar reîncarcă imaginea.
 * Fără el, previzualizarea ar arăta poza veche până la un refresh cu golirea cache-ului.
 */
export function settingFileUrl(setting: Setting): string {
  const path = `/settings/${encodeURIComponent(setting.name)}/file`;

  return `${apiFileUrl(path)}?v=${encodeURIComponent(setting.value ?? '')}`;
}

export function uploadSettingFile(input: { name: string; file: File }): Promise<Setting> {
  return apiUpload(
    `/settings/${encodeURIComponent(input.name)}/file`,
    input.file,
    z.object({ setting: settingSchema }),
  ).then((body) => body.setting);
}

export const SETTINGS_QUERY_KEY = ['settings'] as const;

export const settingsQueryOptions = () =>
  queryOptions({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: () =>
      apiGet('/settings', z.object({ settings: z.array(settingSchema) })).then(
        (body) => body.settings,
      ),
  });

export function createSetting(input: { name: string; value: string }): Promise<Setting> {
  return apiPost('/settings', input, z.object({ setting: settingSchema })).then(
    (body) => body.setting,
  );
}

export function updateSetting(input: { name: string; value: string }): Promise<Setting> {
  return apiPatch(
    // Cheia poate conține caractere de URL (`app.timezone` nu, dar regula nu se scrie
    // pentru cheile de azi).
    `/settings/${encodeURIComponent(input.name)}`,
    { value: input.value },
    z.object({ setting: settingSchema }),
  ).then((body) => body.setting);
}

/**
 * Ce a ieșit din „Ia cursul de la BNR" (#214): aceeași rulare ca a workerului de la 13:05.
 *
 * - `written` — cursul de azi e în setări;
 * - `stale` — BNR n-a publicat încă azi, fișierul e din ziua de dinainte; **nu s-a scris nimic**;
 * - `failed` — BNR n-a răspuns sau n-a răspuns cu fișierul așteptat.
 */
const bnrRatesSchema = z.object({ date: z.string(), eur: z.string(), usd: z.string() });

const refreshResultSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('written'), rates: bnrRatesSchema }),
  z.object({ kind: z.literal('stale'), today: z.string(), rates: bnrRatesSchema }),
  z.object({ kind: z.literal('failed'), message: z.string() }),
]);

export type ExchangeRatesRefresh = z.infer<typeof refreshResultSchema>;

export function refreshExchangeRates(): Promise<ExchangeRatesRefresh> {
  return apiPost(
    '/settings/exchange-rates/refresh',
    {},
    z.object({ result: refreshResultSchema }),
  ).then((body) => body.result);
}

/**
 * Nomenclatoarele administrabile.
 *
 * Cheile sunt aceleași în URL-ul aplicației (`/setari/tipuri-document`) și în cel al
 * API-ului (`/api/catalog/tipuri-document`) — o rută și endpoint-ul ei se citesc la fel.
 */
export const NOMENCLATORS = {
  'profile-client': {
    title: 'Profile de client',
    description: 'Clasificarea comercială atribuită pe fișa clientului.',
    hasCode: false,
    codeLabel: '',
    hasIcon: false,
    hasEquipment: false,
  },
  'tipuri-pachet': {
    title: 'Tipuri de pachet',
    description:
      'Categoriile de servicii din catalog. Codul modulului identifică tipul în integrări, iar iconul îl arată pe fișa clientului.',
    hasCode: true,
    codeLabel: 'Cod modul',
    hasIcon: true,
    // Echipament sau serviciu: decide codul CPV de pe factură (#211).
    hasEquipment: true,
  },
  'tipuri-document': {
    title: 'Tipuri de document',
    description: 'Documentele emise de sistem. Perechea (firmă, tip) determină șablonul.',
    hasCode: true,
    codeLabel: 'Cod',
    hasIcon: false,
    hasEquipment: false,
  },
} as const;

export type NomenclatorKind = keyof typeof NOMENCLATORS;

const nomenclatorItemSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  code: z.string().nullable(),
  /**
   * `z.string()`, nu `z.enum(packageTypeIconKeys)`.
   *
   * ⚠️ Deliberat: serverul îl trimite deja normalizat, dar dacă lista de aici rămâne în urma
   * celei de pe server (două versiuni de frontend în același timp, un deploy la jumătate),
   * un `enum` ar face **tot răspunsul** să pice la `parse` — adică o listă goală de tipuri de
   * pachet din cauza unui simbol. Numele necunoscut cade pe iconul neutru la randare, unde
   * costă doar un simbol.
   *
   * `null` la nomenclatoarele fără icon (profile de client, tipuri de document).
   */
  icon: z.string().nullable(),
  /** Tipul de pachet e echipament (#211). `false` la celelalte nomenclatoare. */
  isEquipment: z.boolean(),
  status: z.boolean(),
});

export type NomenclatorItem = z.infer<typeof nomenclatorItemSchema>;

export const nomenclatorQueryOptions = (kind: NomenclatorKind) =>
  queryOptions({
    queryKey: ['nomenclator', kind] as const,
    queryFn: () =>
      apiGet(
        // Ecranul de administrare le arată și pe cele dezactivate — altfel un rând scos
        // din uz ar dispărea fără urmă și n-ar mai putea fi reactivat.
        `/catalog/${kind}?includeInactive=true`,
        z.object({ items: z.array(nomenclatorItemSchema) }),
      ).then((body) => body.items),
  });

export interface NomenclatorInput {
  readonly name: string;
  readonly code: string;
  /**
   * `null` = nomenclatorul n-are icon. Serverul respinge un icon trimis acolo unde n-are
   * unde să-l pună, în loc să-l ignore tăcut.
   */
  readonly icon: PackageTypeIconKey | null;
  /** Doar la tipurile de pachet; `false` în rest — serverul refuză `true` acolo. */
  readonly isEquipment: boolean;
  readonly status: boolean;
}

export function createNomenclatorItem(
  kind: NomenclatorKind,
  input: NomenclatorInput,
): Promise<NomenclatorItem> {
  return apiPost(`/catalog/${kind}`, input, z.object({ item: nomenclatorItemSchema })).then(
    (body) => body.item,
  );
}

export function updateNomenclatorItem(
  kind: NomenclatorKind,
  id: string,
  input: NomenclatorInput,
): Promise<NomenclatorItem> {
  return apiPatch(`/catalog/${kind}/${id}`, input, z.object({ item: nomenclatorItemSchema })).then(
    (body) => body.item,
  );
}
